/**
 * EventCodeGate — full access flow
 * No localStorage reads or writes. Every user goes through this fresh.
 *
 * Step 1  "event-code"   — Admin-generated 6-digit event code
 * Step 2  "choose"       — New here? or Already registered?
 * Step 3a "returning"    — 5-digit personal Battle OTP (DB verified)
 * Step 3a "resend"       — Enter phone to resend Battle OTP to email
 * Step 3b "new-form"     — Name / email / phone
 * Step 4b "new-otp"      — Verify 5-digit registration OTP
 * Step 5b "new-done"     — Show generated Battle OTP to save
 *
 * Props: onVerified({ name, email, phone }) — called when fully authenticated
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowUpRight, Check, Copy, KeyRound,
  Loader2, RefreshCw, ShieldCheck, UserPlus, Zap,
} from "lucide-react";

const OTP_TTL = 60;

export default function EventCodeGate({ onVerified }) {
  const [step, setStep] = useState("event-code");

  // ── Event code ────────────────────────────────────────────────────────
  const [eventDigits, setEventDigits] = useState(["", "", "", "", "", ""]);
  const [eventChecking, setEventChecking] = useState(false);
  const [devEventCode, setDevEventCode] = useState(null);
  const eventRefs = useRef([]);

  useEffect(() => {
    api.eventCodeCurrent().then((r) => setDevEventCode(r.code || null)).catch(() => {});
  }, []);

  const handleEvChange = (i, v) => {
    const ch = v.replace(/[^0-9A-Za-z]/g, "").slice(-1).toUpperCase();
    const n = [...eventDigits]; n[i] = ch; setEventDigits(n);
    if (ch && i < 5) eventRefs.current[i + 1]?.focus();
  };
  const handleEvKey = (i, e) => { if (e.key === "Backspace" && !eventDigits[i] && i > 0) eventRefs.current[i - 1]?.focus(); };
  const handleEvPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/[^0-9A-Za-z]/g, "").slice(0, 6).toUpperCase();
    if (!txt) return; e.preventDefault();
    const arr = ["", "", "", "", "", ""];
    for (let i = 0; i < txt.length; i++) arr[i] = txt[i];
    setEventDigits(arr);
    eventRefs.current[Math.min(txt.length, 5)]?.focus();
  };
  const submitEventCode = async () => {
    const code = eventDigits.join("");
    if (code.length < 6) { toast.error("Enter all 6 digits"); return; }
    setEventChecking(true);
    try {
      await api.eventCodeVerify(code);
      setStep("choose");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid event code. Ask the Proctorly AI team for the code.");
    } finally { setEventChecking(false); }
  };

  // ── Returning: Battle OTP ─────────────────────────────────────────────
  const [botp, setBotp] = useState(["", "", "", "", ""]);
  const [logging, setLogging] = useState(false);
  const botpRefs = useRef([]);

  const handleBoChange = (i, v) => {
    const ch = v.replace(/\D/g, "").slice(-1);
    const n = [...botp]; n[i] = ch; setBotp(n);
    if (ch && i < 4) botpRefs.current[i + 1]?.focus();
  };
  const handleBoKey = (i, e) => { if (e.key === "Backspace" && !botp[i] && i > 0) botpRefs.current[i - 1]?.focus(); };
  const handleBoPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 5);
    if (!txt) return; e.preventDefault();
    const arr = ["", "", "", "", ""];
    for (let i = 0; i < txt.length; i++) arr[i] = txt[i];
    setBotp(arr);
    botpRefs.current[Math.min(txt.length, 4)]?.focus();
  };
  const loginWithBotp = async () => {
    const code = botp.join("");
    if (code.length < 5) { toast.error("Enter all 5 digits"); return; }
    setLogging(true);
    try {
      const r = await api.userQuickLogin(code);
      toast.success(`Welcome back, ${r.name.split(" ")[0]}!`);
      onVerified({ name: r.name, email: r.email, phone: r.phone });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "OTP not recognised. Check your code or resend it.");
    } finally { setLogging(false); }
  };

  // ── Returning: Resend Battle OTP ──────────────────────────────────────
  const [resendPhone, setResendPhone] = useState("");
  const [resending, setResending] = useState(false);

  const resendBattleOtp = async () => {
    const phone = resendPhone.trim();
    if (!phone) { toast.error("Enter your registered phone number"); return; }
    setResending(true);
    try {
      await api.userResendBattleEntry(phone);
      toast.success("Your Battle OTP has been sent to your registered email!");
      setBotp(["", "", "", "", ""]);
      setStep("returning");
      setTimeout(() => botpRefs.current[0]?.focus(), 80);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Phone number not found. Please register first.");
    } finally { setResending(false); }
  };

  // ── New registration ──────────────────────────────────────────────────
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [regOtp, setRegOtp] = useState(["", "", "", "", ""]);
  const [devOtp, setDevOtp] = useState(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(OTP_TTL);
  const [otpCopied, setOtpCopied] = useState(false);
  const [entryCode, setEntryCode] = useState(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const regRefs = useRef([]);
  const timerRef = useRef(null);
  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCountdown = useCallback(() => {
    setCountdown(OTP_TTL);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => { if (c <= 1) { clearInterval(timerRef.current); return 0; } return c - 1; });
    }, 1000);
  }, []);

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const sendOtp = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) { toast.error("Please fill all fields"); return; }
    if (form.phone.replace(/\D/g, "").length < 7) { toast.error("Enter a valid phone number"); return; }
    setSending(true);
    try {
      const r = await api.otpSend(form);
      setDevOtp(r.dev_otp || null);
      if (r.dev_otp) {
        const arr = r.dev_otp.split("").slice(0, 5);
        while (arr.length < 5) arr.push("");
        setRegOtp(arr);
      }
      startCountdown();
      setStep("new-otp");
      setTimeout(() => regRefs.current[0]?.focus(), 80);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send OTP");
    } finally { setSending(false); }
  };

  const resendOtp = async () => {
    if (countdown > 0) return;
    setSending(true);
    try {
      const r = await api.otpSend(form);
      setDevOtp(r.dev_otp || null);
      if (r.dev_otp) { const arr = r.dev_otp.split("").slice(0, 5); while (arr.length < 5) arr.push(""); setRegOtp(arr); }
      startCountdown();
    } catch { toast.error("Failed to regenerate OTP"); }
    finally { setSending(false); }
  };

  const handleRegChange = (i, v) => {
    const ch = v.replace(/\D/g, "").slice(-1);
    const n = [...regOtp]; n[i] = ch; setRegOtp(n);
    if (ch && i < 4) regRefs.current[i + 1]?.focus();
  };
  const handleRegKey = (i, e) => { if (e.key === "Backspace" && !regOtp[i] && i > 0) regRefs.current[i - 1]?.focus(); };
  const handleRegPaste = (e) => {
    const txt = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 5);
    if (!txt) return; e.preventDefault();
    const arr = ["", "", "", "", ""];
    for (let i = 0; i < txt.length; i++) arr[i] = txt[i];
    setRegOtp(arr);
    regRefs.current[Math.min(txt.length, 4)]?.focus();
  };

  const verifyNewUser = async () => {
    const code = regOtp.join("");
    if (code.length < 5) { toast.error("Enter the 5-digit code"); return; }
    setVerifying(true);
    try {
      const r = await api.otpVerify({ ...form, otp: code });
      setEntryCode(r.entry_code || null);
      toast.success(`Verified, ${form.name.split(" ")[0]}!`);
      setStep("new-done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid OTP");
    } finally { setVerifying(false); }
  };

  const timerColor = countdown > 30 ? "text-emerald-400" : countdown > 10 ? "text-yellow-400" : "text-[color:var(--kl-red)]";
  const timerBg    = countdown > 30 ? "bg-emerald-500/10 border-emerald-500/30" : countdown > 10 ? "bg-yellow-500/10 border-yellow-500/30" : "bg-red-500/10 border-red-500/30";

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-white relative"
      style={{ background: "var(--kl-navy-deep)" }}>

      <a href="/" className="absolute top-6 left-6 flex items-center gap-2 font-mono-kl text-xs uppercase tracking-widest text-white/40 hover:text-white transition z-20">
        <ArrowLeft className="w-4 h-4" /> Back to home
      </a>

      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(230,57,70,0.6), transparent 60%)" }} />

      <div className="relative z-10 w-full max-w-sm">

        {/* ── STEP 1: Event Code ────────────────────────────────── */}
        {step === "event-code" && (
          <>
            <div className="text-center mb-8">
              <div className="font-mono-kl text-[11px] uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-3">
                Proctorly AI · Event Access
              </div>
              <h1 className="font-display text-4xl uppercase tracking-tight text-white">Enter Event Code</h1>
              <p className="text-white/45 text-sm mt-3">
                This experience is for AI Summit attendees only.<br />
                Enter the 6-digit code shown on the event screen.
              </p>
            </div>

            {devEventCode && (
              <div className="mb-5 rounded-lg border border-[color:var(--kl-red)]/40 bg-[color:var(--kl-red)]/8 p-3 text-center">
                <div className="font-mono-kl text-[10px] uppercase tracking-widest text-[color:var(--kl-red)] mb-1">Demo Code</div>
                <div className="font-display text-3xl tracking-[0.4em] text-white">{devEventCode}</div>
              </div>
            )}

            <div className="flex gap-2 justify-between mb-5">
              {eventDigits.map((d, i) => (
                <input key={i} ref={(el) => (eventRefs.current[i] = el)}
                  inputMode="numeric" maxLength={1} value={d}
                  onChange={(e) => handleEvChange(i, e.target.value)}
                  onKeyDown={(e) => handleEvKey(i, e)}
                  onPaste={i === 0 ? handleEvPaste : undefined}
                  className="otp-dark flex-1" />
              ))}
            </div>

            <button onClick={submitEventCode} disabled={eventChecking || eventDigits.join("").length < 6}
              className="btn-submit w-full justify-center disabled:opacity-40">
              {eventChecking
                ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Checking…</>
                : <>Enter the Event <Zap className="w-4 h-4" /></>}
            </button>
          </>
        )}

        {/* ── STEP 2: New or Returning ──────────────────────────── */}
        {step === "choose" && (
          <>
            <div className="text-center mb-8">
              <div className="font-mono-kl text-[11px] uppercase tracking-[0.3em] text-[color:var(--kl-red)] mb-3">Proctorly AI</div>
              <h1 className="font-display text-4xl uppercase tracking-tight text-white">Welcome!</h1>
              <p className="text-white/45 text-sm mt-3">First time here or have you registered before?</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => { setStep("returning"); setTimeout(() => botpRefs.current[0]?.focus(), 80); }}
                className="w-full rounded-lg border border-[color:var(--kl-red)]/40 bg-[color:var(--kl-red)]/8 hover:bg-[color:var(--kl-red)]/15 transition p-5 text-left flex items-center gap-4"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(230,57,70,0.15)", border: "1px solid rgba(230,57,70,0.4)" }}>
                  <KeyRound className="w-5 h-5 text-[color:var(--kl-red)]" />
                </div>
                <div>
                  <div className="font-display text-lg uppercase text-white">Already Registered</div>
                  <div className="text-white/45 text-xs mt-0.5">Sign in with my 5-digit Battle OTP</div>
                </div>
              </button>

              <button
                onClick={() => setStep("new-form")}
                className="w-full rounded-lg border border-white/15 bg-white/3 hover:bg-white/7 transition p-5 text-left flex items-center gap-4"
              >
                <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.2)" }}>
                  <UserPlus className="w-5 h-5 text-white/60" />
                </div>
                <div>
                  <div className="font-display text-lg uppercase text-white">New Here</div>
                  <div className="text-white/45 text-xs mt-0.5">Register in under a minute</div>
                </div>
              </button>
            </div>

            <button onClick={() => setStep("event-code")}
              className="w-full text-center mt-5 font-mono-kl text-[10px] uppercase tracking-widest text-white/30 hover:text-white/60 transition">
              ← Back
            </button>
          </>
        )}

        {/* ── STEP 3a: Returning — Enter Battle OTP ────────────── */}
        {step === "returning" && (
          <>
            <div className="text-center mb-7">
              <h1 className="font-display text-3xl uppercase tracking-tight text-white">
                Enter your <span className="text-[color:var(--kl-red)]">Battle OTP</span>
              </h1>
              <p className="text-white/45 text-sm mt-2">
                The 5-digit code from your registration email.
              </p>
            </div>

            <div className="flex gap-2.5 justify-between mb-5">
              {botp.map((d, i) => (
                <input key={i} ref={(el) => (botpRefs.current[i] = el)}
                  inputMode="numeric" maxLength={1} value={d}
                  onChange={(e) => handleBoChange(i, e.target.value)}
                  onKeyDown={(e) => handleBoKey(i, e)}
                  onPaste={i === 0 ? handleBoPaste : undefined}
                  className="otp-dark flex-1" />
              ))}
            </div>

            <button onClick={loginWithBotp} disabled={logging || botp.join("").length < 5}
              className="btn-submit w-full justify-center disabled:opacity-40">
              {logging
                ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Verifying…</>
                : <>Sign In <Zap className="w-4 h-4" /></>}
            </button>

            <div className="mt-4 text-center">
              <button onClick={() => setStep("resend")}
                className="font-mono-kl text-[10px] uppercase tracking-widest text-[color:var(--kl-red)]/70 hover:text-[color:var(--kl-red)] transition">
                Don't have the code? Resend it →
              </button>
            </div>

            <button onClick={() => setStep("choose")}
              className="w-full text-center mt-3 font-mono-kl text-[10px] uppercase tracking-widest text-white/30 hover:text-white/60 transition">
              ← Back
            </button>
          </>
        )}

        {/* ── STEP 3a alt: Resend Battle OTP ───────────────────── */}
        {step === "resend" && (
          <>
            <div className="mb-6">
              <h1 className="font-display text-3xl uppercase tracking-tight text-white">
                Resend <span className="text-[color:var(--kl-red)]">Battle OTP</span>
              </h1>
              <p className="text-white/45 text-sm mt-2">
                Enter your registered phone number. We'll send your code to the email you used when you first registered.
              </p>
            </div>

            <div className="mb-5">
              <label className="form-label">Registered Phone <span className="req">*</span></label>
              <input className="input-dark" value={resendPhone}
                onChange={(e) => setResendPhone(e.target.value)}
                placeholder="+91 98xxxxxxxx" autoComplete="tel" />
            </div>

            <div className="flex items-center justify-between">
              <button onClick={() => setStep("returning")} className="btn-ghost-dark text-xs">← Back</button>
              <button onClick={resendBattleOtp} disabled={resending} className="btn-submit">
                {resending
                  ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Sending…</>
                  : <>Send Code <ArrowUpRight className="w-4 h-4" /></>}
              </button>
            </div>
          </>
        )}

        {/* ── STEP 3b: New — Registration Form ─────────────────── */}
        {step === "new-form" && (
          <>
            <div className="mb-6">
              <h1 className="font-display text-3xl uppercase tracking-tight text-white">
                Quick <span className="text-[color:var(--kl-red)]">Registration</span>
              </h1>
              <p className="text-white/45 text-sm mt-2">
                One-time setup. You'll get a personal OTP to sign in instantly next time.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="form-label">Full Name <span className="req">*</span></label>
                <input className="input-dark" value={form.name}
                  onChange={(e) => setF("name", e.target.value)}
                  placeholder="Ada Lovelace" autoComplete="name" />
              </div>
              <div>
                <label className="form-label">Email <span className="req">*</span></label>
                <input type="email" className="input-dark" value={form.email}
                  onChange={(e) => setF("email", e.target.value)}
                  placeholder="you@example.com" autoComplete="email" />
              </div>
              <div>
                <label className="form-label">Phone <span className="req">*</span></label>
                <input className="input-dark" value={form.phone}
                  onChange={(e) => setF("phone", e.target.value)}
                  placeholder="+91 98xxxxxxxx" autoComplete="tel" />
              </div>
            </div>

            <div className="flex items-center justify-between mt-6">
              <button onClick={() => setStep("choose")} className="btn-ghost-dark text-xs">← Back</button>
              <button onClick={sendOtp} disabled={sending} className="btn-submit">
                {sending ? "Generating…" : "Get OTP"} <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}

        {/* ── STEP 4b: New — Verify OTP ────────────────────────── */}
        {step === "new-otp" && (
          <>
            <div className="mb-5">
              <h1 className="font-display text-3xl uppercase tracking-tight text-white">
                Your <span className="text-[color:var(--kl-red)]">OTP</span> is here.
              </h1>
              <p className="text-white/45 text-sm mt-1">
                Sent to <span className="text-white">{form.phone}</span>
              </p>
            </div>

            {devOtp && (
              <div className="mb-5 rounded-lg border border-[color:var(--kl-red)]/50 bg-[color:var(--kl-red)]/10 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono-kl text-[10px] uppercase tracking-widest text-[color:var(--kl-red)]">OTP</span>
                  <span className={`font-mono-kl text-xs px-2 py-0.5 rounded border font-bold ${timerBg} ${timerColor}`}>{countdown}s</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-display text-4xl tracking-[0.3em] text-white">{devOtp}</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(devOtp).then(() => { setOtpCopied(true); setTimeout(() => setOtpCopied(false), 2000); }); }}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-white/20 text-white/60 hover:text-white transition text-xs font-mono-kl">
                    {otpCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    {otpCopied ? "Copied" : "Copy"}
                  </button>
                </div>
                {countdown === 0 && (
                  <button onClick={resendOtp} disabled={sending}
                    className="mt-2 flex items-center gap-1.5 text-[color:var(--kl-red)] text-xs font-mono-kl uppercase tracking-widest hover:opacity-80">
                    <RefreshCw className="w-3 h-3" /> New OTP
                  </button>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-between mb-6">
              {regOtp.map((d, i) => (
                <input key={i} ref={(el) => (regRefs.current[i] = el)}
                  inputMode="numeric" maxLength={1} value={d}
                  onChange={(e) => handleRegChange(i, e.target.value)}
                  onKeyDown={(e) => handleRegKey(i, e)}
                  onPaste={handleRegPaste}
                  className="otp-dark flex-1" />
              ))}
            </div>

            <div className="flex items-center justify-between">
              <button onClick={() => setStep("new-form")} className="btn-ghost-dark text-xs">← Back</button>
              <button onClick={verifyNewUser} disabled={verifying} className="btn-submit">
                {verifying ? "Verifying…" : "Verify & Enter"} <Zap className="w-4 h-4" />
              </button>
            </div>
          </>
        )}

        {/* ── STEP 5b: New — Show Battle OTP to save ───────────── */}
        {step === "new-done" && (
          <div className="flex flex-col items-center text-center gap-5">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-emerald-400" />
            </div>
            <div>
              <h2 className="font-display text-3xl uppercase tracking-tight text-white">
                You're in, <span className="text-[color:var(--kl-red)]">{form.name.split(" ")[0]}</span>!
              </h2>
              <p className="text-white/45 text-sm mt-2">Registration complete.</p>
            </div>

            {entryCode && (
              <div className="w-full rounded-lg border border-[color:var(--kl-red)]/50 bg-[color:var(--kl-red)]/10 p-5 text-left">
                <div className="flex items-center gap-2 mb-1">
                  <KeyRound className="w-4 h-4 text-[color:var(--kl-red)]" />
                  <span className="font-mono-kl text-[10px] uppercase tracking-widest text-[color:var(--kl-red)]">
                    Your Battle OTP — Save this!
                  </span>
                </div>
                <p className="text-white/40 text-xs mb-3">
                  Use this code to sign in instantly next time. Valid for 8 hours.
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-display text-5xl tracking-[0.4em] text-white">{entryCode}</span>
                  <button
                    onClick={() => { navigator.clipboard.writeText(entryCode).then(() => { setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); }); }}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded border border-white/20 text-white/60 hover:text-white transition text-xs font-mono-kl">
                    {codeCopied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    {codeCopied ? "Copied" : "Copy"}
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={() => onVerified({ name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() })}
              className="btn-submit w-full">
              Continue <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        )}

      </div>
    </div>
  );
}
